import { useState } from "react";
import {
  Button,
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Avatar,
  Link,
} from "@mui/material";
import { FileUploadInfo } from "../../../shared/ui/file-upload-info";
import {
  FILE_SIZE_LIMIT_BYTES,
  getFileSizeLimitError,
} from "../../../shared/constants/file-upload-limits";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { useRef } from "react";
import {
  useGetEventSpeakersQuery,
  useLazyExportEventSpeakersXlsxQuery,
  useCreateEventSpeakerMutation,
  useUpdateEventSpeakerMutation,
  useDeleteEventSpeakerMutation,
  useUploadEventSpeakerAvatarMutation,
  useGetEventSpeakerSkillsQuery,
} from "../../../entities/admin-events";

const INSTAGRAM_URL_MAX_LENGTH = 2048;

/** Возвращает текст ошибки или null, если контакты заполнены корректно */
const getContactsError = (phoneNumber: string, instagramUrl: string) => {
  if (phoneNumber && !/^\d{10,15}$/.test(phoneNumber)) {
    return "Телефон должен содержать от 10 до 15 цифр с кодом страны";
  }
  if (instagramUrl) {
    if (instagramUrl.length > INSTAGRAM_URL_MAX_LENGTH) {
      return `Ссылка на Instagram длиннее ${INSTAGRAM_URL_MAX_LENGTH} символов`;
    }
    try {
      const url = new URL(instagramUrl);
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        throw new Error();
      }
    } catch {
      return "Укажите ссылку на Instagram целиком, например https://instagram.com/username";
    }
  }
  return null;
};

export const EventSpeakersManagement = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<number | null>(
    null
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [patronymic, setPatronymic] = useState("");
  const [about, setAbout] = useState("");
  const [userId, setUserId] = useState("");
  const [skills, setSkills] = useState<number[]>([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const { data: speakersData, isLoading, refetch } = useGetEventSpeakersQuery();
  const { data: skillsData } = useGetEventSpeakerSkillsQuery();
  const [exportXlsx, { isFetching: isExporting }] =
    useLazyExportEventSpeakersXlsxQuery();

  const [createSpeaker, { isLoading: isCreating }] =
    useCreateEventSpeakerMutation();
  const [updateSpeaker, { isLoading: isUpdating }] =
    useUpdateEventSpeakerMutation();
  const [deleteSpeaker, { isLoading: isDeleting }] =
    useDeleteEventSpeakerMutation();
  const [uploadAvatar] = useUploadEventSpeakerAvatarMutation();

  // Обработчики модальных окон
  const handleOpenCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    resetForm();
  };

  const handleOpenUpdateDialog = (speakerId: number) => {
    const speaker = speakersData?.speakers.find((s) => s.id === speakerId);
    if (speaker) {
      setSelectedSpeakerId(speakerId);
      setFirstName(speaker.firstName);
      setLastName(speaker.lastName);
      setPatronymic(speaker.patronymic || "");
      setAbout(speaker.about);
      setUserId(""); // в примере API нет userId в ответе
      setSkills(speaker.skills.map((skill) => skill.id));
      setPhoneNumber(speaker.phoneNumber ?? "");
      setInstagramUrl(speaker.instagramUrl ?? "");
      setIsUpdateDialogOpen(true);
    }
  };

  const handleCloseUpdateDialog = () => {
    setIsUpdateDialogOpen(false);
    resetForm();
  };

  const handleOpenDeleteDialog = (speakerId: number) => {
    setSelectedSpeakerId(speakerId);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedSpeakerId(null);
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setPatronymic("");
    setAbout("");
    setUserId("");
    setSkills([]);
    setPhoneNumber("");
    setInstagramUrl("");
    setSelectedSpeakerId(null);
    setAvatarFile(null);
    setAvatarPreview(null);
    if (avatarInputRef.current) {
      avatarInputRef.current.value = "";
    }
  };

  // Обработчик для аватара
  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const sizeError = getFileSizeLimitError(file, FILE_SIZE_LIMIT_BYTES.DEFAULT);
      if (sizeError) {
        alert(sizeError);
        if (avatarInputRef.current) {
          avatarInputRef.current.value = "";
        }
        return;
      }

      setAvatarFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    if (avatarInputRef.current) {
      avatarInputRef.current.value = "";
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportXlsx().unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "speakers.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Не удалось выгрузить спикеров");
    }
  };

  const handleSkillsChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as unknown as number[];
    setSkills(value);
  };

  // Обработчики CRUD операций
  const handleCreate = async () => {
    if (!lastName || !firstName) {
      alert("Пожалуйста, заполните обязательные поля");
      return;
    }

    const contactsError = getContactsError(phoneNumber, instagramUrl.trim());
    if (contactsError) {
      alert(contactsError);
      return;
    }

    try {
      const result = await createSpeaker({
        firstName,
        lastName,
        patronymic,
        about,
        userId: userId || undefined,
        skills,
        phoneNumber: phoneNumber || undefined,
        instagramUrl: instagramUrl.trim() || undefined,
      }).unwrap();

      if (result.result && avatarFile && result.speaker.id) {
        const formData = new FormData();
        formData.append("file", avatarFile);
        await uploadAvatar({ id: result.speaker.id, file: formData }).unwrap();
      }

      handleCloseCreateDialog();
      refetch();
    } catch (error) {
      console.error("Failed to create speaker:", error);
      alert("Не удалось создать спикера");
    }
  };

  const handleUpdate = async () => {
    if (!selectedSpeakerId || !lastName || !firstName) {
      alert("Пожалуйста, заполните обязательные поля");
      return;
    }

    const contactsError = getContactsError(phoneNumber, instagramUrl.trim());
    if (contactsError) {
      alert(contactsError);
      return;
    }

    try {
      await updateSpeaker({
        id: selectedSpeakerId,
        body: {
          firstName,
          lastName,
          patronymic,
          about,
          skills,
          phoneNumber: phoneNumber || undefined,
          instagramUrl: instagramUrl.trim() || undefined,
        },
      }).unwrap();

      if (avatarFile) {
        const formData = new FormData();
        formData.append("file", avatarFile);
        await uploadAvatar({ id: selectedSpeakerId, file: formData }).unwrap();
      }

      handleCloseUpdateDialog();
      refetch();
    } catch (error) {
      console.error("Failed to update speaker:", error);
      alert("Не удалось обновить спикера");
    }
  };

  const handleDelete = async () => {
    if (!selectedSpeakerId) return;

    try {
      await deleteSpeaker(selectedSpeakerId).unwrap();
      handleCloseDeleteDialog();
      refetch();
    } catch (error) {
      console.error("Failed to delete speaker:", error);
      alert("Не удалось удалить спикера");
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Управление спикерами мероприятий</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? "Выгрузка..." : "Выгрузить в Excel"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Добавить спикера
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Фото</TableCell>
              <TableCell>ФИО</TableCell>
              <TableCell>Навыки</TableCell>
              <TableCell>Контакты</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Загрузка...
                </TableCell>
              </TableRow>
            ) : speakersData?.speakers && speakersData.speakers.length > 0 ? (
              speakersData.speakers.map((speaker) => (
                <TableRow key={speaker.id}>
                  <TableCell>{speaker.id}</TableCell>
                  <TableCell>
                    <Avatar
                      src={speaker.photoUrl}
                      alt={`${speaker.firstName} ${speaker.lastName}`}
                    />
                  </TableCell>
                  <TableCell>
                    {`${speaker.lastName} ${speaker.firstName} ${
                      speaker.patronymic || ""
                    }`}
                  </TableCell>
                  <TableCell>
                    {speaker.skills.map((skill) => skill.name).join(", ")}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {speaker.phoneNumber && (
                        <Link href={`tel:+${speaker.phoneNumber}`}>
                          +{speaker.phoneNumber}
                        </Link>
                      )}
                      {speaker.instagramUrl && (
                        <Link
                          href={speaker.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Instagram
                        </Link>
                      )}
                      {!speaker.phoneNumber && !speaker.instagramUrl && "—"}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenUpdateDialog(speaker.id)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(speaker.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания спикера */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Создать спикера</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Фамилия"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <TextField
              label="Имя"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextField
              label="Отчество"
              value={patronymic}
              onChange={(e) => setPatronymic(e.target.value)}
            />
            <TextField
              label="О спикере"
              multiline
              rows={4}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
            />
            <TextField
              label="ID пользователя"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
            <TextField
              label="Телефон"
              placeholder="77001234567"
              helperText="10–15 цифр с кодом страны, без пробелов и скобок"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
              inputProps={{ inputMode: "numeric", maxLength: 15 }}
            />
            <TextField
              label="Instagram"
              placeholder="https://instagram.com/username"
              value={instagramUrl}
              onChange={(e) => setInstagramUrl(e.target.value)}
              inputProps={{ maxLength: INSTAGRAM_URL_MAX_LENGTH }}
            />
            <FormControl>
              <InputLabel>Навыки</InputLabel>
              <Select
                multiple
                value={skills}
                onChange={handleSkillsChange}
                input={<OutlinedInput label="Навыки" />}
              >
                {skillsData?.skills?.map((skill) => (
                  <MenuItem key={skill.id} value={skill.id}>
                    {skill.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Фото спикера
              </Typography>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleAvatarChange}
                ref={avatarInputRef}
                id="avatar-upload"
              />
              <label htmlFor="avatar-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Загрузить фото
                </Button>
              </label>
              <FileUploadInfo
                type="avatar"
                recommendedSize="300x300 пикселей"
              />
              {avatarPreview && (
                <Box sx={{ mt: 2, position: "relative", width: 200 }}>
                  <img
                    src={avatarPreview}
                    alt="Предпросмотр фото"
                    style={{ width: "100%", borderRadius: "4px" }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleClearAvatar}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bgcolor: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Отмена</Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог редактирования спикера */}
      <Dialog
        open={isUpdateDialogOpen}
        onClose={handleCloseUpdateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Редактировать спикера</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Фамилия"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <TextField
              label="Имя"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextField
              label="Отчество"
              value={patronymic}
              onChange={(e) => setPatronymic(e.target.value)}
            />
            <TextField
              label="О спикере"
              multiline
              rows={4}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
            />
            <TextField
              label="Телефон"
              placeholder="77001234567"
              helperText="10–15 цифр с кодом страны, без пробелов и скобок"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
              inputProps={{ inputMode: "numeric", maxLength: 15 }}
            />
            <TextField
              label="Instagram"
              placeholder="https://instagram.com/username"
              value={instagramUrl}
              onChange={(e) => setInstagramUrl(e.target.value)}
              inputProps={{ maxLength: INSTAGRAM_URL_MAX_LENGTH }}
            />
            <FormControl>
              <InputLabel>Навыки</InputLabel>
              <Select
                multiple
                value={skills}
                onChange={handleSkillsChange}
                input={<OutlinedInput label="Навыки" />}
              >
                {skillsData?.skills?.map((skill) => (
                  <MenuItem key={skill.id} value={skill.id}>
                    {skill.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Фото спикера
              </Typography>
              {selectedSpeakerId &&
                speakersData?.speakers &&
                !avatarPreview && (
                  <Box sx={{ mb: 2, width: 200 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Текущее фото:
                    </Typography>
                    <Avatar
                      src={
                        speakersData.speakers.find(
                          (s) => s.id === selectedSpeakerId
                        )?.photoUrl
                      }
                      alt="Текущее фото"
                      sx={{ width: 150, height: 150 }}
                    />
                  </Box>
                )}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleAvatarChange}
                ref={avatarInputRef}
                id="avatar-upload-edit"
              />
              <label htmlFor="avatar-upload-edit">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  {selectedSpeakerId &&
                  speakersData?.speakers?.find(
                    (s) => s.id === selectedSpeakerId
                  )?.photoUrl
                    ? "Изменить фото"
                    : "Загрузить фото"}
                </Button>
              </label>
              <FileUploadInfo
                type="avatar"
                recommendedSize="300x300 пикселей"
              />
              {avatarPreview && (
                <Box sx={{ mt: 2, position: "relative", width: 200 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Новое фото:
                  </Typography>
                  <img
                    src={avatarPreview}
                    alt="Предпросмотр фото"
                    style={{ width: "100%", borderRadius: "4px" }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleClearAvatar}
                    sx={{
                      position: "absolute",
                      top: 20,
                      right: 0,
                      bgcolor: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>Отмена</Button>
          <Button onClick={handleUpdate} disabled={isUpdating}>
            Обновить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить этого спикера?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Отмена</Button>
          <Button onClick={handleDelete} color="error" disabled={isDeleting}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
